import React, { useState, useEffect } from "react";
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Card
} from "@mui/material";
import api from "../../api";

const MemoryMetrics = () => {
  const [decks, setDecks] = useState([]);
  const [selectedDeck, setSelectedDeck] = useState("");
  const [memoryData, setMemoryData] = useState({
    Apprentice: 0,
    Guru: 0,
    Master: 0,
    Enlightened: 0,
    Burned: 0
  });
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("default");

  useEffect(() => {
    fetchDecks();
    // fetch aggregated metrics by default
    fetchMemoryMetrics(); 
  }, []);

  const fetchDecks = async () => {
    try {
      const response = await api.get("/user-deck-metrics");
      const publicDecks = response.data.publicDecks || [];
      const userDecks = response.data.userDecks || [];

      // Use a Map to remove duplicates (based on _id)
      const deckMap = new Map();
      [...publicDecks, ...userDecks].forEach((deck) => {
        deckMap.set(deck._id, deck);
      });

      // Convert map back to an array
      const uniqueDecks = Array.from(deckMap.values());
      setDecks(uniqueDecks);
    } catch (error) {
      console.error("Error fetching decks:", error);
    }
  };

  const fetchMemoryMetrics = async (deckId = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await api.post(
        "/get-user-memory-metrics",
        { deckId },
        { headers }
      );

      const levels = response.data.data[0] || {};
      const coalescedLevels = coalesceLevels(levels);
      setMemoryData(coalescedLevels);
    } catch (error) {
      console.error("Error fetching memory metrics:", error);
      // reset on error
      setMemoryData({
        Apprentice: 0,
        Guru: 0,
        Master: 0,
        Enlightened: 0,
        Burned: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const coalesceLevels = (levels) => {
    return {
      Apprentice: (levels["1"] || 0) + (levels["2"] || 0) + (levels["3"] || 0),
      Guru: (levels["4"] || 0) + (levels["5"] || 0),
      Master: levels["6"] || 0,
      Enlightened: levels["7"] || 0,
      Burned: levels["8"] || 0
    };
  };

  const handleModeChange = (event) => {
    const newMode = event.target.value;
    setMode(newMode);
    setSelectedDeck("");

    if (newMode === "default") {
      fetchMemoryMetrics();
    }
  };

  const handleDeckChange = (event) => {
    const deckId = event.target.value;
    setSelectedDeck(deckId);
    fetchMemoryMetrics(deckId);
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        maxWidth: "90%",
        margin: "0% 10% 0% 10%",
        mt: 5,
        border: "1px solid black",
        borderRadius: "5px",
        p: 2
      }}
    >
      <Typography
        variant="h5"
        sx={{
          backgroundColor: "#e3f2fd",
          textAlign: "center",
          padding: 2,
          borderRadius: 1,
          mb: 2
        }}
      >
        Your flashcard stages
      </Typography>

      <Box sx={{ maxWidth: 900, margin: "auto", p: 3, textAlign: "center" }}>
        <FormControl sx={{ minWidth: 200, mb: 2 }}>
          <InputLabel id="mode-select-label">Select Mode</InputLabel>
          <Select
            labelId="mode-select-label"
            id="mode-select"
            label="Select Mode"
            value={mode}
            onChange={handleModeChange}
          >
            <MenuItem value="default">Aggregated (All Decks)</MenuItem>
            <MenuItem value="deck">Deck-Specific</MenuItem>
          </Select>
        </FormControl>

        {mode === "deck" && (
          <FormControl sx={{ minWidth: 200, mb: 2, ml: 2 }}>
            <InputLabel id="deck-select-label">Select Deck</InputLabel>
            <Select
              labelId="deck-select-label"
              id="deck-select"
              label="Select Deck"
              value={selectedDeck}
              onChange={handleDeckChange}
            >
                {console.log('Debug: decks', decks)}
              {decks.map((deck) => (
                <MenuItem key={deck._id} value={deck._id}>
                  {deck.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {loading ? (
          <Typography variant="h6">Loading...</Typography>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 3 }}>
            {Object.entries(memoryData).map(([label, value]) => (
              <Card
                key={label} // use label as key so it's stable
                sx={{
                  flex: 1,
                  p: 2,
                  textAlign: "center",
                  backgroundColor: getColor(label),
                  color: "white",
                  fontWeight: "bold"
                }}
              >
                <Typography variant="h4">{value}</Typography>
                <Typography>{label}</Typography>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Color helper function
const getColor = (label) => {
  switch (label) {
    case "Apprentice":
      return "#E91E63"; // Pink
    case "Guru":
      return "#9C27B0"; // Purple
    case "Master":
      return "#3F51B5"; // Blue
    case "Enlightened":
      return "#03A9F4"; // Light Blue
    case "Burned":
      return "#424242"; // Dark Gray
    default:
      return "#757575"; // Default Gray
  }
};

export default MemoryMetrics;
